// One clinical-alert source for both Patient Record > Medical History and
// Chairside. Keep decision wording and severity identical across surfaces.
export function computeMedicalHistoryAlerts(data, context = {}) {
  const out = [];
  const bpAlert = computeBpAlert(extractBpReading(data, context));
  if (bpAlert) out.push(bpAlert);

  if (data?.allergies?.drug?.yes && data.allergies.drug.specify) {
    out.push({ label: "Drug allergy", detail: data.allergies.drug.specify });
  }
  if (data?.allergies?.anesthetic?.yes) {
    out.push({ label: "Local anesthetic reaction", detail: data.allergies.anesthetic.specify || "history present" });
  }
  if (data?.allergies?.latex) {
    out.push({ label: "Latex allergy", detail: "use latex-free gloves & dam" });
  }
  if (data?.meds?.blood_thinners?.yes) {
    out.push({ label: "Anticoagulant", detail: data.meds.blood_thinners.specify || "on blood thinners — bleeding risk" });
  }
  if (data?.meds?.bisphosphonates?.yes) {
    out.push({ label: "Bisphosphonates", detail: data.meds.bisphosphonates.specify || "ONJ risk on extractions" });
  }
  if (data?.meds?.steroids_long_term?.yes) {
    out.push({ label: "Long-term steroids", detail: data.meds.steroids_long_term.specify || "consider stress-dose protocol" });
  }
  if (data?.meds?.chemo_radiation?.yes) {
    out.push({ label: "Chemo / head-neck radiation", detail: data.meds.chemo_radiation.specify || "ORN risk" });
  }
  if (data?.cardiac?.premed_required) {
    out.push({ label: "Premedication required", detail: data.cardiac.premed_advised_by ? `advised by ${data.cardiac.premed_advised_by}` : "antibiotic prophylaxis" });
  } else {
    if (data?.cardiac?.artificial_valve) out.push({ label: "Artificial heart valve", detail: "evaluate for premed" });
    if (data?.cardiac?.rheumatic_fever) out.push({ label: "Rheumatic fever history", detail: "evaluate for premed" });
    if (data?.cardiac?.joint_replacement?.yes) {
      out.push({ label: "Joint replacement", detail: data.cardiac.joint_replacement.specify || "consult orthopedic" });
    }
  }
  if (data?.cardiac?.heart_attack?.yes) {
    out.push({ label: "Past heart attack", detail: data.cardiac.heart_attack.when || "history present" });
  }
  if (data?.cardiac?.stroke?.yes) {
    out.push({ label: "Past stroke", detail: data.cardiac.stroke.when || "history present" });
  }
  if (data?.cardiac?.pacemaker) {
    out.push({ label: "Pacemaker", detail: "avoid ultrasonic scaler" });
  }
  if (data?.pregnancy?.applicable && data.pregnancy.pregnant) {
    out.push({ label: "Pregnant", detail: data.pregnancy.weeks ? `~${data.pregnancy.weeks} weeks` : "defer non-urgent radiographs" });
  }
  if (data?.conditions?.diabetes) {
    out.push({ label: "Diabetes", detail: "Diabetic patient. Verify control status and last meal. Healing and infection risk elevated." });
  }
  if (data?.conditions?.hypertension) {
    out.push({ label: "Hypertension history", detail: "Hypertensive patient. Confirm BP at chairside before treatment." });
  }
  if (data?.conditions?.asthma) {
    out.push({ label: "Asthma", detail: "Asthmatic patient. Avoid sulfite-containing local anesthetics. Ensure rescue inhaler accessible." });
  }
  if (data?.conditions?.kidney_disease) {
    out.push({ label: "Kidney disease", detail: "Renal disease. Adjust medication doses. Bleeding risk if on dialysis." });
  }
  if (data?.conditions?.liver_disease) {
    out.push({ label: "Liver disease / hepatitis", detail: "Hepatic disease. Bleeding risk and altered drug metabolism. PPE precautions for hepatitis." });
  }
  if (data?.conditions?.epilepsy) {
    out.push({ label: "Epilepsy / seizures", detail: "Seizure history. Verify current medications and last seizure date." });
  }
  if (data?.conditions?.thyroid_disorder) {
    out.push({ label: "Thyroid disorder", detail: "Thyroid disorder. Hyperthyroid: caution with epinephrine. Hypothyroid: altered healing." });
  }
  if (data?.conditions?.tuberculosis) out.push({ label: "Active TB risk", detail: "PPE + airborne precautions" });
  if (data?.conditions?.hiv) out.push({ label: "HIV / AIDS", detail: "standard universal precautions" });
  if (data?.conditions?.covid_recent) out.push({ label: "COVID in last 30 days", detail: "consider deferring elective tx" });
  if (data?.conditions?.bleeding_disorder) out.push({ label: "Bleeding disorder", detail: "hemostasis plan needed" });
  if (data?.conditions?.dental_anxiety) out.push({ label: "Severe dental anxiety", detail: "sedation / behavioral plan" });
  return out;
}

function computeBpAlert(bp) {
  if (!bp) return null;
  const { systolic, diastolic } = bp;
  const reading = `${systolic}/${diastolic}`;
  if (systolic >= 180 || diastolic >= 110) {
    return {
      label: `BP ${reading}`,
      detail: "HYPERTENSIVE CRISIS. Refer immediately to ER or physician. Do not proceed with any dental treatment, including emergency palliative care.",
      severity: "blocking",
    };
  }
  if ((systolic >= 160 && systolic <= 179) || (diastolic >= 100 && diastolic <= 109)) {
    return {
      label: `BP ${reading}`,
      detail: "Stage 2 hypertension. Defer elective treatment. Refer to physician for medication review. Emergency-only care with monitoring.",
    };
  }
  if ((systolic >= 140 && systolic <= 159) || (diastolic >= 90 && diastolic <= 99)) {
    return {
      label: `BP ${reading}`,
      detail: "Stage 1 hypertension. Verify diagnosis and current medications. Proceed with caution if controlled.",
    };
  }
  return null;
}

function extractBpReading(form, context = {}) {
  const candidates = [
    context.visitVitals,
    context.history?.latest_vitals,
    context.history?.visit_vitals,
    context.history?.check_in_vitals,
    context.patient?.latest_vitals,
    context.patient?.visit_vitals,
    context.patient?.check_in_vitals,
    form?.vitals,
    form?.visit_vitals,
    form?.check_in_vitals,
    form?.bp,
  ].filter(Boolean);

  for (const source of candidates) {
    const parsed = parseBpReading(source);
    if (parsed) return parsed;
  }
  return null;
}

function parseBpReading(source) {
  if (typeof source === "string") {
    const match = source.match(/(\d{2,3})\s*\/\s*(\d{2,3})/);
    if (!match) return null;
    return normalizeBp(match[1], match[2]);
  }
  if (!source || typeof source !== "object") return null;
  const combined = source.bp || source.blood_pressure || source.bloodPressure || source.reading;
  if (combined) {
    const parsed = parseBpReading(String(combined));
    if (parsed) return parsed;
  }
  const systolic = source.systolic ?? source.systolic_bp ?? source.bp_systolic ?? source.blood_pressure_systolic;
  const diastolic = source.diastolic ?? source.diastolic_bp ?? source.bp_diastolic ?? source.blood_pressure_diastolic;
  return normalizeBp(systolic, diastolic);
}

function normalizeBp(systolic, diastolic) {
  const sys = Number.parseInt(systolic, 10);
  const dia = Number.parseInt(diastolic, 10);
  if (!Number.isFinite(sys) || !Number.isFinite(dia)) return null;
  if (sys < 40 || dia < 30) return null;
  return { systolic: sys, diastolic: dia };
}
